import { Grid } from "@mui/material";
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { getAllUsers, getUserById, updateUser, deleteUser, createUser, sendUserEmail } from '../services/userService';
import { useAuth } from '../context/AuthContext';
import { isValidEmail, isValidName, isValidPhone, isValidPassword } from '../utils/validators';
import { isPosEnabledForStore } from '../utils/subsystemAccess';


const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'employee',
    first_name: '',
    last_name: '',
    phone: '',
    user_pin: '',
    user_key: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [actionLoading, setActionLoading] = useState(false);
  const [actionSuccess, setActionSuccess] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [createdUserId, setCreatedUserId] = useState(null);
  const [emailSending, setEmailSending] = useState(false);
  const [emailType, setEmailType] = useState('password-reset');

  const { user: currentUser } = useAuth();
  const posEnabled = isPosEnabledForStore(currentUser);

  const canEditPinKey = useMemo(() => {
    const actorRole = currentUser?.role;
    const targetRole = formData.role;

    if (actorRole !== "admin" && actorRole !== "manager") return false;
    if (actorRole === "manager" && targetRole === "admin") return false;
    return true;
  }, [currentUser?.role, formData.role]);

  const filteredUsers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return users;

    return users.filter((user) => {
      const fields = [
        user.name,
        user.email,
        user.role,
        user.first_name,
        user.last_name,
        user.phone,
      ];
      return fields.some((value) => String(value || '').toLowerCase().includes(query));
    });
  }, [users, searchQuery]);

  // Fetch users
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const data = await getAllUsers();
        setUsers(data);
        setError('');
      } catch (error) {
        console.error('Error fetching users:', error);
        setError('Failed to load users. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Clear field error when user types
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };

  const validateForm = (isCreate = false) => {
    const newErrors = {};

    if (!formData.name) {
      newErrors.name = 'Name is required';
    } else if (!isValidName(formData.name)) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    if (isCreate) {
      if (!formData.email) {
        newErrors.email = 'Email is required';
      } else if (!isValidEmail(formData.email)) {
        newErrors.email = 'Invalid email format';
      }

      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (!isValidPassword(formData.password)) {
        newErrors.password = 'Password must be at least 8 characters with at least one letter and one number';
      }
    }

    if (formData.phone && !isValidPhone(formData.phone)) {
      newErrors.phone = 'Invalid phone format';
    }

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Open edit dialog
  const handleEditClick = async (user) => {
    console.log("API:", process.env.REACT_APP_API_URL);
    setSelectedUser(user);
    setFormErrors({});
    setActionSuccess('');
    setEmailType('password-reset');
    setEditDialogOpen(true);

    try {
      setActionLoading(true);

      // fetch full user (includes user_pin/user_key)
      const fullUser = await getUserById(user.id);

      setFormData({
        name: fullUser.name || '',
        email: fullUser.email || '',
        role: fullUser.role || 'employee',
        first_name: fullUser.first_name || '',
        last_name: fullUser.last_name || '',
        phone: fullUser.phone || '',
        password: '',
        user_pin: fullUser.user_pin ?? '',
        user_key: fullUser.user_key ?? '',
      });
    } catch (e) {
      console.error(e);
      setFormErrors({ submit: "Failed to load PIN/Key for this user" });
    } finally {
      setActionLoading(false);
    }
  };

  // Open delete dialog
  const handleDeleteClick = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  // Open create dialog
  const handleCreateClick = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'employee',
      first_name: '',
      last_name: '',
      phone: '',
      user_pin: '',
      user_key: ''
    });
    setFormErrors({});
    setActionSuccess('');
    setCreatedUserId(null);
    setEmailType('account-created');
    setCreateDialogOpen(true);
  };

  // Handle update user
  const handleUpdateUser = async () => {
    if (!validateForm()) return;

    setActionLoading(true);

    try {
      const payload = {
        name: formData.name,
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone,
        role: formData.role,
        ...(formData.password ? { password: formData.password } : {}),
      };

      if (canEditPinKey && posEnabled) {
        payload.user_pin = formData.user_pin; // "" allowed (backend can convert to null)
        payload.user_key = formData.user_key;
      }

      const updatedUser = await updateUser(selectedUser.id, payload);

      setUsers(users.map(u => (u.id === selectedUser.id ? { ...u, ...updatedUser } : u)));

      setActionSuccess("User updated successfully");

      setTimeout(() => setEditDialogOpen(false), 1500);
    } catch (error) {
      console.error("Error updating user:", error);
      setFormErrors({ submit: "Failed to update user" });
    } finally {
      setActionLoading(false);
    }
  };

  // Handle delete user
  const handleDeleteUser = async () => {
    setActionLoading(true);

    try {
      await deleteUser(selectedUser.id);

      // Update users list
      setUsers(users.filter(user => user.id !== selectedUser.id));

      setDeleteDialogOpen(false);
    } catch (error) {
      setFormErrors({
        submit: error?.message || 'Failed to delete user',
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Handle create user
  const handleCreateUser = async () => {
    if (!validateForm(true)) {
      return;
    }

    setActionLoading(true);

    try {
      const result = await createUser({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone,
        role: formData.role
      });

      if (result?.user) {
        // Refresh users list
        const newUsers = await getAllUsers();
        setUsers(newUsers);

        setCreatedUserId(result.user.id);
        setActionSuccess('User created successfully. Use Send email to send their account email.');
      } else {
        setFormErrors({ submit: 'Failed to create user' });
      }
    } catch (error) {
      console.error('Error creating user:', error);
      setFormErrors({ submit: 'Failed to create user' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendUserEmail = async (userId, type) => {
    if (!userId) return;

    if (type === 'account-created' && !formData.password) {
      setFormErrors({ submit: 'Enter the password to include it in the account created email.' });
      setActionSuccess('');
      return;
    }

    setEmailSending(true);
    setFormErrors({});
    setActionSuccess('');

    try {
      await sendUserEmail(userId, {
        type,
        password: type === 'account-created' ? formData.password : undefined,
      });
      setActionSuccess('Email sent.');
    } catch (error) {
      setFormErrors({ submit: error?.message || 'Failed to send email' });
    } finally {
      setEmailSending(false);
    }
  };

  return (
    <Box sx={{ px: '10%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
        <Typography fontWeight={500} variant="h4" component="h1">
          User Management
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            size="small"
            placeholder="Search by name, email, or role"
            value={searchQuery}
            onChange={handleSearchChange}
            sx={{ width: 280 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateClick}
          >
            Add User
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <TableContainer>
            <Table
              size="small"
              sx={{
                '& .MuiTableCell-root': {
                  py: 1.25,
                  px: 1.5,
                },
                '& .MuiTableCell-root:first-of-type': {
                  pl: 1,
                },
                '& .MuiTableCell-root:last-of-type': {
                  pr: 1,
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      {searchQuery.trim() ? 'No users match your search.' : 'No users found.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>{user.name}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.role}</TableCell>
                        <TableCell>
                          {new Date(user.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            color="primary"
                            onClick={() => handleEditClick(user)}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={() => handleDeleteClick(user)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredUsers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ px: 2 }}
          />
        </Paper>
      )}

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          {actionSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {actionSuccess}
            </Alert>
          )}

          {formErrors.submit && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formErrors.submit}
            </Alert>
          )}

          <Box component="form" noValidate sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="name"
              label="Full Name"
              name="name"
              value={formData.name}
              onChange={handleFormChange}
              error={!!formErrors.name}
              helperText={formErrors.name}
              disabled={actionLoading}
            />
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="first_name"
                  label="First Name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleFormChange}
                  disabled={actionLoading}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="last_name"
                  label="Last Name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleFormChange}
                  disabled={actionLoading}
                />
              </Grid>
            </Grid>
            <TextField
              margin="normal"
              fullWidth
              id="phone"
              label="Phone Number"
              name="phone"
              value={formData.phone}
              onChange={handleFormChange}
              error={!!formErrors.phone}
              helperText={formErrors.phone}
              disabled={actionLoading}
            />
            <TextField
              margin="normal"
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              value={formData.email}
              disabled={true} // Email cannot be changed
            />
            <FormControl fullWidth margin="normal">
              <InputLabel id="role-label">Role</InputLabel>
              <Select
                labelId="role-label"
                id="role"
                name="role"
                value={formData.role}
                label="Role"
                onChange={handleFormChange}
                disabled={actionLoading}
              >
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="manager">Manager</MenuItem>
                <MenuItem value="employee">Employee</MenuItem>
              </Select>
            </FormControl>
            <TextField
              margin="normal"
              fullWidth
              name="password"
              label="New Password (leave blank to keep current)"
              type="password"
              id="password"
              value={formData.password}
              onChange={handleFormChange}
              error={!!formErrors.password}
              helperText={
                formErrors.password
                  || (emailType === 'account-created'
                    ? 'Enter the password to include it in the account created email'
                    : 'Leave blank to keep current')
              }
              disabled={actionLoading}
            />
          </Box>
          {posEnabled && (
          <Paper variant="outlined" sx={{ mt: 2, p: 2, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              POS Access
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="user_pin"
                  label="User PIN (4 digits)"
                  name="user_pin"
                  value={formData.user_pin}
                  onChange={handleFormChange}
                  disabled={actionLoading || !canEditPinKey}
                  inputProps={{ inputMode: "numeric", pattern: "[0-9]*", maxLength: 4 }}
                  helperText={!canEditPinKey ? "Only admins/managers can edit (managers can't edit admin PIN)." : "Leave blank to clear"}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="user_key"
                  label="User Key (4 digits)"
                  name="user_key"
                  value={formData.user_key}
                  onChange={handleFormChange}
                  disabled={actionLoading || !canEditPinKey}
                  inputProps={{ inputMode: "numeric", pattern: "[0-9]*", maxLength: 4 }}
                  helperText="Leave blank to clear"
                />
              </Grid>
            </Grid>
          </Paper>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 220, mr: 'auto' }}>
            <InputLabel id="edit-email-type-label">Email</InputLabel>
            <Select
              labelId="edit-email-type-label"
              label="Email"
              value={emailType}
              onChange={(e) => setEmailType(e.target.value)}
              disabled={actionLoading || emailSending}
            >
              <MenuItem value="account-created">Account created</MenuItem>
              <MenuItem value="password-updated">Password updated</MenuItem>
              <MenuItem value="password-reset">Password reset</MenuItem>
            </Select>
          </FormControl>
          <Button
            onClick={() => handleSendUserEmail(selectedUser?.id, emailType)}
            disabled={actionLoading || emailSending || !selectedUser?.id}
          >
            {emailSending ? <CircularProgress size={24} /> : 'Send email'}
          </Button>
          <Button onClick={() => setEditDialogOpen(false)} disabled={actionLoading || emailSending}>
            Cancel
          </Button>
          <Button onClick={handleUpdateUser} disabled={actionLoading || emailSending}>
            {actionLoading ? <CircularProgress size={24} /> : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {selectedUser?.name}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button onClick={handleDeleteUser} color="error" disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create User</DialogTitle>
        <DialogContent>
          {actionSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {actionSuccess}
            </Alert>
          )}

          {formErrors.submit && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formErrors.submit}
            </Alert>
          )}

          <Box component="form" noValidate sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="name"
              label="Full Name"
              name="name"
              value={formData.name}
              onChange={handleFormChange}
              error={!!formErrors.name}
              helperText={formErrors.name}
              disabled={actionLoading}
            />
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="first_name"
                  label="First Name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleFormChange}
                  disabled={actionLoading}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="last_name"
                  label="Last Name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleFormChange}
                  disabled={actionLoading}
                />
              </Grid>
            </Grid>
            <TextField
              margin="normal"
              fullWidth
              id="phone"
              label="Phone Number"
              name="phone"
              value={formData.phone}
              onChange={handleFormChange}
              error={!!formErrors.phone}
              helperText={formErrors.phone}
              disabled={actionLoading}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              value={formData.email}
              onChange={handleFormChange}
              error={!!formErrors.email}
              helperText={formErrors.email}
              disabled={actionLoading}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel id="role-label">Role</InputLabel>
              <Select
                labelId="role-label"
                id="role"
                name="role"
                value={formData.role}
                label="Role"
                onChange={handleFormChange}
                disabled={actionLoading}
              >
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="manager">Manager</MenuItem>
                <MenuItem value="employee">Employee</MenuItem>
              </Select>
            </FormControl>
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              value={formData.password}
              onChange={handleFormChange}
              error={!!formErrors.password}
              helperText={formErrors.password}
              disabled={actionLoading}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)} disabled={actionLoading || emailSending}>
            {createdUserId ? 'Close' : 'Cancel'}
          </Button>
          {createdUserId && (
            <Button
              onClick={() => handleSendUserEmail(createdUserId, 'account-created')}
              disabled={actionLoading || emailSending}
            >
              {emailSending ? <CircularProgress size={24} /> : 'Send email'}
            </Button>
          )}
          {!createdUserId && (
            <Button onClick={handleCreateUser} disabled={actionLoading}>
              {actionLoading ? <CircularProgress size={24} /> : 'Create'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};



export default UserManagement;
